import { Logger } from '@frontend/logger';
import { TransactionClient, TransactionExtendedClient } from '@frontend/transaction/api';
import { Transaction } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useState } from 'react';

import { useAppSelector } from '../../../redux/store';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';
import { canContinueWithExistingDraftTransaction } from './existing-tracking-number.utils';

interface ViewProps {
    trackingNumber: string;
    setTrackingNumber: (value: string | null) => void;
    isGeneratingTrackingNumber: boolean;
    onGenerateTrackingNumber: () => Promise<void>;
    existingTransactions: Transaction[] | null;
    currentUserId: string | null;
    continuingTransactionId: string | null;
    submitDisabled: boolean;
    onSubmit: () => Promise<void>;
    onContinueWithExistingTransaction: (transaction: Transaction) => Promise<void>;
    refreshExistingTransactions: () => Promise<void>;
    onBack: (type: ScreenType) => void;
}

/**
 * Finds transactions with the given tracking number, excluding the wizard's current remote transaction when present.
 * That way continuing with / already owning that draft does not block the user; other matches still do.
 */
const findExistingTransactions = async (accountId: string, trackingNumber: string, currentTransactionId?: string): Promise<Transaction[]> => {
    const response = await TransactionClient.fetchTransactions({
        account_id: accountId,
        tracking_number: trackingNumber
    });

    return response.results.filter((transaction) => {
        if (transaction.tracking_number !== trackingNumber) return false;
        if (currentTransactionId && transaction.id === currentTransactionId) return false;
        return true;
    });
};

const resolveGeneratedTrackingNumber = (response: unknown): string | null => {
    if (!response) return null;
    if (typeof response === 'string') return response.trim() || null;
    if (typeof response !== 'object') return null;

    const value = response as Record<string, unknown>;
    const candidates = [value.unique_identifier, value.uniqueIdentifier, value.tracking_number, value.trackingNumber, value.id];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate.trim();
    }

    return null;
};

export const useEnterTrackingNumber = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const { syncTransaction, localUpdateTransaction, clearRemoteTransaction } = useWizardTransaction({ wizardId: props.wizard_id, store });
    const currentUserId = useAppSelector((state) => state.users.current?.id ?? null);
    const [trackingNumber, setTrackingNumberState] = useState<string>(store?.data.trackingNumber || store?.data.transaction?.tracking_number || '');
    const [existingTransactions, setExistingTransactions] = useState<Transaction[] | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [continuingTransactionId, setContinuingTransactionId] = useState<string | null>(null);
    const [isGeneratingTrackingNumber, setIsGeneratingTrackingNumber] = useState<boolean>(false);

    const setTrackingNumber = (value: string | null) => {
        setTrackingNumberState(value ?? '');
        setExistingTransactions(null);
    };

    const runTrackingNumberConflictCheck = async (normalizedTrackingNumber: string, currentTransactionId?: string) => {
        if (!store) return [];

        const matches = await findExistingTransactions(store.wizard.account_id, normalizedTrackingNumber, currentTransactionId ?? store.data.transaction?.id);
        setExistingTransactions(matches.length > 0 ? matches : null);
        return matches;
    };

    const onGenerateTrackingNumber = async () => {
        if (!store || isGeneratingTrackingNumber || isSubmitting) return;
        setIsGeneratingTrackingNumber(true);
        try {
            const response = await TransactionClient.fetchTransactionUniqueIdentifier(store.wizard.account_id);
            const generatedTrackingNumber = resolveGeneratedTrackingNumber(response);
            if (generatedTrackingNumber) {
                setTrackingNumberState(generatedTrackingNumber);
                setExistingTransactions(null);
            }
        } catch (error) {
            Logger.error('Failed to generate tracking number', error);
        } finally {
            setIsGeneratingTrackingNumber(false);
        }
    };

    const proceedToNextStep = (normalizedTrackingNumber: string, transaction: Transaction) => {
        if (!store) return;

        setExistingTransactions(null);
        updateWizardData({
            trackingNumber: normalizedTrackingNumber,
            transaction
        });

        const nextStep = store.steps.find((step) => step.screen === ScreenType.ENTER_TRACKING_NUMBER && step.trigger === ScreenTrigger.SUBMIT);
        navigateToNextStep(nextStep);
    };

    const refreshExistingTransactions = async () => {
        const normalizedTrackingNumber = trackingNumber.trim();
        if (!store || normalizedTrackingNumber.length === 0) {
            setExistingTransactions(null);
            return;
        }

        try {
            await runTrackingNumberConflictCheck(normalizedTrackingNumber, store.data.transaction?.id);
        } catch (error) {
            Logger.error('Failed to refresh existing transactions', error);
        }
    };

    const onContinueWithExistingTransaction = async (transaction: Transaction) => {
        if (!store || isSubmitting || continuingTransactionId || !canContinueWithExistingDraftTransaction(transaction, currentUserId)) {
            return;
        }

        setContinuingTransactionId(transaction.id);
        try {
            const extendedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(store.wizard.account_id, transaction.id);
            const adoptedTrackingNumber = (extendedTransaction.tracking_number ?? trackingNumber).trim();

            clearRemoteTransaction();
            await localUpdateTransaction(extendedTransaction);
            setTrackingNumberState(adoptedTrackingNumber);
            proceedToNextStep(adoptedTrackingNumber, extendedTransaction);
        } catch (error) {
            Logger.error('Failed to continue with existing transaction', error);
        } finally {
            setContinuingTransactionId(null);
        }
    };

    const onSubmit = async () => {
        const normalizedTrackingNumber = trackingNumber.trim();
        if (!store || isSubmitting || isGeneratingTrackingNumber || continuingTransactionId || normalizedTrackingNumber.length === 0) {
            return;
        }

        setIsSubmitting(true);
        try {
            const alreadyWarnedAboutDuplicates = existingTransactions !== null && existingTransactions.length > 0;

            if (!alreadyWarnedAboutDuplicates) {
                const matches = await runTrackingNumberConflictCheck(normalizedTrackingNumber, store.data.transaction?.id);
                if (matches.length > 0) {
                    return;
                }
            }

            const transaction = await syncTransaction({
                tracking_number: normalizedTrackingNumber
            });

            proceedToNextStep(normalizedTrackingNumber, transaction);
        } catch (error) {
            Logger.error('Failed to submit tracking number', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        trackingNumber,
        setTrackingNumber,
        isGeneratingTrackingNumber,
        onGenerateTrackingNumber,
        existingTransactions,
        currentUserId,
        continuingTransactionId,
        submitDisabled: isSubmitting || isGeneratingTrackingNumber || !!continuingTransactionId || trackingNumber.trim().length === 0,
        onSubmit,
        onContinueWithExistingTransaction,
        refreshExistingTransactions,
        onBack
    };
};

export default useEnterTrackingNumber;
